import React, { useState } from 'react';
import '../styles/components/SWOTGroup.css';

interface SWOTItem {
  id: number;
  finding: string;
  swot_type_id: number;
  section_id?: number;
}

interface SWOTGroupProps {
  type: number;
  items: SWOTItem[];
  evaluationId: string;
  searchText?: string;
  onEdit: (item: SWOTItem) => void;
  onDelete: (itemId: number) => void;
}

const SWOTGroup: React.FC<SWOTGroupProps> = ({ type, items, evaluationId, searchText, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(true);
  const [editingItem, setEditingItem] = useState<SWOTItem | null>(null);
  const [editText, setEditText] = useState('');
  
  const getTypeLabel = (typeId: number) => {
    switch(typeId) {
      case 1: return 'Strengths';
      case 2: return 'Weaknesses';
      case 3: return 'Opportunities';
      case 4: return 'Threats';
      default: return 'Insights';
    }
  };
  
  const toggleExpanded = () => {
    setExpanded(!expanded);
  };
  
  const handleEditClick = (item: SWOTItem) => {
    setEditingItem(item);
    setEditText(item.finding);
  };

  const handleSaveEdit = () => {
    if (editingItem) {
      onEdit({
        ...editingItem,
        finding: editText
      });
      setEditingItem(null);
      setEditText('');
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditText('');
  };
  
  const highlightText = (text: string) => {
    if (!searchText || !text) return text;
    
    const parts = text.split(new RegExp(`(${searchText})`, 'gi'));
    
    return parts.map((part, index) => {
      if (part.toLowerCase() === searchText.toLowerCase()) {
        return <mark key={index} className="search-highlight">{part}</mark>;
      }
      return part;
    });
  };
  
  if (items.length === 0) return null;
  
  return (
    <div className="swot-group" data-type={type}>
      <div className="swot-header">
        <div className="swot-header-title" onClick={toggleExpanded}>
          <span className="material-icons">
            {expanded ? 'expand_less' : 'expand_more'}
          </span>
          <h4>{getTypeLabel(type)}</h4>
        </div>
      </div>
      
      {expanded && (
        <div className="swot-items">
          {items.map((item) => (
            <li id={`swot-${item.id}`} key={item.id} className="swot-item">
              {editingItem?.id === item.id ? (
                <div className="swot-edit-form">
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    placeholder="Enter insight content..."
                  />
                  <div className="swot-edit-actions">
                    <button onClick={handleSaveEdit} className="save-button">
                      <span className="material-icons">check</span>
                      Save
                    </button>
                    <button onClick={handleCancelEdit} className="cancel-button">
                      <span className="material-icons">close</span>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="swot-item-content">
                  {highlightText(item.finding)}
                  </div>
                  <div className="swot-item-actions">
                    <button onClick={() => handleEditClick(item)} aria-label="Edit insight">
                      <span className="material-icons">edit</span>
                    </button>
                    <button onClick={() => onDelete(item.id)} aria-label="Delete insight">
                      <span className="material-icons">delete</span>
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </div>
      )}
    </div>
  );
};

export default SWOTGroup; 